import { BlockProps } from '@/components/Block/Block';
import P from '@/components/Redactie/P/P';
import { useBlockLocalUserData } from '@/hooks/useBlockLocalUserData';
import { useBlockRemoteUserData } from '@/hooks/useBlockRemoteUserData';
import {
    mdiCloseThick,
    mdiCheckDecagram,
    mdiCircleOutline,
    mdiRadioboxMarked,
    mdiTimelineQuestion,
    mdiCheckCircle,
} from '@mdi/js';
import Icon from '@mdi/react';
import { Checkbox, FormControlLabel } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import ListEditor from '@/components/Redactie/ListEditor/ListEditor';

import styles from './QuizBlock.module.sass';
import BlockEditorContainer from '@/components/Block/BlockEditorContainer';

interface QuizBlockData {
    question: string;
    answers: Array<QuizBlockAnswer>;
}

interface QuizBlockLocalData {
    pickedAnswer: number | null;
}

interface QuizBlockAnswer {
    text: string;
    is_correct: boolean;
}

const QuizBlock = ({
    subjectId,
    id,
    data,
    uuid,
    updatedAt,
    isEditable,
    isEditorPreview,
    isAnimated,
    onFinished,
    onEdited,
}: BlockProps) => {
    const { userDataRemote, submitUserDataRemote } = useBlockRemoteUserData(id);
    const { userDataLocal, setUserDataLocal } =
        useBlockLocalUserData<QuizBlockLocalData>(
            subjectId,
            id,
            updatedAt,
            isEditorPreview,
            {
                pickedAnswer: null,
            },
        );

    const [blockData, setBlockData] = useState<QuizBlockData>({
        question: data.question ?? 'Wat is waar?',
        answers: data.answers ?? [
            { text: 'Dit', is_correct: true },
            { text: 'Dat', is_correct: false },
        ],
    });
    const pickedCorrectAnswer = useMemo(() => {
        if (userDataLocal.pickedAnswer === null) return null;
        return blockData.answers[userDataLocal.pickedAnswer].is_correct;
    }, [userDataLocal.pickedAnswer, blockData]);
    // Get an array of percentages for each answer
    const answerPercentages = useMemo(() => {
        if (!userDataRemote?.summary || userDataRemote?.submitted_data === null)
            return null;
        return blockData.answers.map((_, index) => {
            const count = userDataRemote.summary.find(
                (item) => item.data === index,
            )?.count;
            // Calculate percentage of total count, if count is a number and not undefined
            if (typeof count === 'number') {
                return Math.round((count / userDataRemote.count) * 100);
            } else {
                return 0;
            }
        });
    }, [userDataRemote, blockData]);

    // Go to next block when user has picked an answer
    useEffect(() => {
        if (isEditable)
            setUserDataLocal({
                ...userDataLocal,
                pickedAnswer: null,
            });
        if (userDataLocal.pickedAnswer === null) return;
        onFinished?.();
    }, [isEditable, userDataLocal.pickedAnswer]);

    // Save changes to block data
    useEffect(() => {
        if (!isEditable) return;
        onEdited?.({
            uuid,
            data: JSON.parse(
                JSON.stringify({
                    ...blockData,
                    answers: blockData.answers.filter(
                        (answer) => !!answer?.text && answer.text.length > 0,
                    ),
                }),
            ),
            attachments: {},
        });
    }, [blockData]);

    return (
        <div
            className={`${styles['quizblock']} ${
                isEditable ? styles['quizblock--editable'] : ''
            } ${isAnimated ? styles['quizblock--animated'] : ''} ${
                userDataLocal.pickedAnswer !== null
                    ? styles['quizblock--picked']
                    : ''
            } ${pickedCorrectAnswer ? styles['quizblock--correct'] : ''}`}
        >
            {isEditable && (
                <BlockEditorContainer className="bg-secondary-300">
                    <div>
                        <P
                            className="font-bold"
                            isEditable={isEditable}
                            onChange={(value) => {
                                setBlockData({
                                    ...blockData,
                                    question: value,
                                });
                            }}
                            placeholder="Quizvraag"
                            value={blockData.question}
                            rows={1}
                        >
                            {blockData.question}
                        </P>
                    </div>
                    <ListEditor<QuizBlockAnswer>
                        list={blockData.answers}
                        onSaved={(list) => {
                            setBlockData({
                                ...blockData,
                                answers: list,
                            });
                        }}
                        createNewItem={() => ({
                            text: '',
                            is_correct: false,
                        })}
                        validateItem={(item) => ({
                            isValid: !!item && item.text.length > 0,
                            validationText:
                                'Voeg een tekst toe of verwijder dit antwoord',
                        })}
                        validateList={(list) => ({
                            isValid: list.some(
                                (item) => item.is_correct === true,
                            ),
                            validationText:
                                'Voeg minstens 1 correct antwoord toe',
                        })}
                        minItems={2}
                        maxItems={5}
                        listDescription={blockData.question}
                        listDisplayName="Antwoorden"
                        listItemDisplayName="Antwoord"
                        listSummaryItemData={(item) => ({
                            displayName: item.text,
                            icon: item.is_correct
                                ? mdiCheckCircle
                                : mdiCircleOutline,
                        })}
                        listItemTemplate={QuizBlockAnswerEditor}
                    />
                </BlockEditorContainer>
            )}

            {!isEditable && (
                <fieldset>
                    <legend
                        className={`${styles['quizblock__header']} ${
                            userDataLocal.pickedAnswer !== null
                                ? styles['quizblock__header--picked']
                                : ''
                        }`}
                    >
                        <Icon path={mdiTimelineQuestion} size={1.25} />
                        <p className={styles['quizblock__header__question']}>
                            {blockData.question}
                        </p>
                    </legend>
                    {
                        <ol
                            className={`${styles['quizblock__options']} ${
                                userDataLocal.pickedAnswer !== null
                                    ? styles['quizblock__options--picked']
                                    : ''
                            }`}
                            aria-label={`Vraag: ${blockData.question}. ${blockData.answers.length} keuzes.`}
                        >
                            {blockData.answers
                                .filter((answer) => answer?.text?.length > 0)
                                .map((answer, index) => (
                                    <li key={index}>
                                        <QuizBlockAnswerButton
                                            answer={answer}
                                            answerPercentages={
                                                answerPercentages
                                            }
                                            index={index}
                                            pickedAnswer={
                                                userDataLocal.pickedAnswer
                                            }
                                            setPickedAnswer={(
                                                index: number,
                                            ) => {
                                                setUserDataLocal({
                                                    ...userDataLocal,
                                                    pickedAnswer: index,
                                                });
                                            }}
                                            submitUserDataRemote={
                                                submitUserDataRemote
                                            }
                                        />
                                    </li>
                                ))}
                        </ol>
                    }
                </fieldset>
            )}
        </div>
    );
};

const QuizBlockAnswerEditor = (
    index: number,
    item: QuizBlockAnswer,
    setItem: (item: QuizBlockAnswer) => void,
) => (
    <div className="rounded-md bg-white outline outline-2 outline-slate-200">
        <P
            isEditable
            maxLength={96}
            onChange={(value) => {
                setItem({
                    ...item,
                    text: value,
                    is_correct: value?.length > 0 ? item.is_correct : false,
                });
            }}
            placeholder={`Antwoord ${index + 1}`}
            value={item.text}
            rows={1}
        >
            {item.text}
        </P>
        <div className="mx-2">
            <FormControlLabel
                value="end"
                onChange={(event) =>
                    setItem({
                        ...item,
                        is_correct:
                            item?.text?.length === 0
                                ? false
                                : (event.target as HTMLInputElement).checked,
                    })
                }
                control={
                    <Checkbox
                        size="small"
                        disabled={item?.text?.length === 0}
                        checked={item.is_correct}
                    />
                }
                label={<span className="text-sm font-bold">Correct</span>}
                labelPlacement="end"
            />
        </div>
    </div>
);

interface QuizBlockAnswerButtonProps {
    answer: QuizBlockAnswer;
    answerPercentages: number[] | null;
    index: number;
    pickedAnswer: number | null;
    setPickedAnswer: (index: number) => void;
    submitUserDataRemote: (index: number) => void;
}

const QuizBlockAnswerButton = ({
    answer,
    index,
    pickedAnswer,
    setPickedAnswer,
    submitUserDataRemote,
}: QuizBlockAnswerButtonProps) => {
    const isAnAnswerPicked = pickedAnswer !== null;
    const isThisAnswerPicked = pickedAnswer === index;
    const isCorrectButUnpickedAnswer =
        pickedAnswer !== null && pickedAnswer !== index && answer.is_correct;
    const isCorrectlyPickedAnswer = pickedAnswer === index && answer.is_correct;
    const isIncorrectlyPickedAnswer =
        pickedAnswer === index && !answer.is_correct;

    return (
        <button
            className={`${styles['quizblock__options__option']} ${
                pickedAnswer !== index
                    ? styles['quizblock__options__option--discarded']
                    : ''
            } ${
                isThisAnswerPicked
                    ? styles['quizblock__options__option--picked']
                    : ''
            } ${
                isCorrectButUnpickedAnswer
                    ? styles[
                          'quizblock__options__option--correct-but-discarded'
                      ]
                    : ''
            } ${
                isCorrectlyPickedAnswer
                    ? styles['quizblock__options__option--correct']
                    : ''
            } ${
                isIncorrectlyPickedAnswer
                    ? styles['quizblock__options__option--wrong']
                    : ''
            }`}
            aria-pressed={isThisAnswerPicked}
            aria-disabled={isAnAnswerPicked}
            onClick={() => {
                if (pickedAnswer !== null) return;
                setPickedAnswer(index);
                submitUserDataRemote(index);
            }}
        >
            <div className={styles['quizblock__options__option__text']}>
                <Icon
                    size={1}
                    path={
                        isThisAnswerPicked
                            ? mdiRadioboxMarked
                            : mdiCircleOutline
                    }
                    className={
                        isThisAnswerPicked || !isAnAnswerPicked
                            ? ''
                            : 'opacity-50'
                    }
                />
                <div>
                    {answer.text}

                    {/* Only show badge if needed */}
                    {isAnAnswerPicked &&
                        (isCorrectButUnpickedAnswer ||
                            isCorrectlyPickedAnswer ||
                            isIncorrectlyPickedAnswer) && (
                            <div
                                className={
                                    styles[
                                        'quizblock__options__option__text__badge'
                                    ]
                                }
                            >
                                <Icon
                                    size={0.6}
                                    path={
                                        isCorrectButUnpickedAnswer ||
                                        isCorrectlyPickedAnswer
                                            ? mdiCheckDecagram
                                            : mdiCloseThick
                                    }
                                />
                                {(isCorrectButUnpickedAnswer ||
                                    isCorrectlyPickedAnswer) &&
                                    'Juist antwoord'}
                                {isIncorrectlyPickedAnswer &&
                                    'Volgende keer beter!'}
                            </div>
                        )}
                </div>
            </div>
            {pickedAnswer === index && (
                <span className="sr-only">(door jou gekozen)</span>
            )}
        </button>
    );
};

export default QuizBlock;
